import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/Common";
import { Bell, MapPin, Mail, ChefHat, ExternalLink, CheckCheck, Building2, Settings as SettingsIcon } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [region, setRegion] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [a, s] = await Promise.all([api.get("/alerts"), api.get("/settings")]);
    setAlerts(a.data);
    setRegion(s.data.preferred_region || "");
    setLoading(false);
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const markAll = async () => { await api.post("/alerts/read-all"); toast.success("Tutti gli avvisi segnati come letti"); load(); };
  const clearAll = async () => { await api.delete("/alerts"); toast.success("Avvisi eliminati"); load(); };

  const unread = alerts.filter((a) => !a.read).length;

  return (
    <div className="p-6 max-w-[1200px]">
      <PageHeader title="Avvisi Nuove Offerte" icon={Bell}
        subtitle={region ? `Zona preferita: ${region} — ti avviso quando compaiono nuove offerte di cucina` : "Imposta una zona preferita per ricevere avvisi mirati"}
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="alerts-read-all" onClick={markAll} disabled={unread === 0}
              className="px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 bg-white/5 border border-white/10 hover:border-amber-500/30 text-gray-200 disabled:opacity-40">
              <CheckCheck className="w-4 h-4" /> Segna letti
            </button>
            <button data-testid="alerts-clear" onClick={clearAll} disabled={alerts.length === 0}
              className="px-4 py-2.5 rounded-lg text-sm bg-white/5 border border-white/10 hover:border-red-500/30 text-gray-300 disabled:opacity-40">
              Svuota
            </button>
          </div>
        }
      />

      {!region && (
        <div className="card p-4 mb-5 flex items-center justify-between gap-3 border-amber-500/20 bg-amber-500/[0.04]">
          <div className="flex items-center gap-3 text-sm text-gray-300">
            <SettingsIcon className="w-5 h-5 text-amber-400 shrink-0" />
            Nessuna zona preferita impostata. Scegli un Bundesland nelle Impostazioni per attivare gli avvisi automatici.
          </div>
          <Link to="/impostazioni" data-testid="alerts-goto-settings" className="btn-copper px-4 py-2 rounded-lg text-sm shrink-0">Imposta zona</Link>
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center"><Spinner className="w-7 h-7" /></div>
      ) : alerts.length === 0 ? (
        <EmptyState icon={Bell} title="Nessun avviso" hint="Quando lo scraper trova nuove offerte di cucina nella tua zona preferita, compaiono qui." />
      ) : (
        <div className="space-y-2.5">
          {alerts.map((a, i) => (
            <div key={a.id} data-testid={`alert-${a.id}`}
              className={`card p-4 flex items-center gap-4 fade-up ${!a.read ? "border-amber-500/30 bg-amber-500/[0.03]" : ""}`}
              style={{ animationDelay: `${(i % 12) * 25}ms` }}>
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                <ChefHat className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {!a.read && <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />}
                  <span className="font-medium text-gray-100 truncate">{a.role} · {a.title}</span>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mt-1">
                  <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{a.employer}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{a.city}, {a.region}</span>
                  {a.email && <span className="flex items-center gap-1 text-amber-400/90 font-mono"><Mail className="w-3.5 h-3.5" />{a.email}</span>}
                </div>
              </div>
              {a.apply_url && (
                <a href={a.apply_url} target="_blank" rel="noreferrer" data-testid={`alert-apply-${a.id}`}
                  className="text-sm text-sky-400 hover:text-sky-300 flex items-center gap-1.5 shrink-0">
                  <ExternalLink className="w-3.5 h-3.5" /> Annuncio
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
